/* Preview-only transfer film. Production artwork and geometry remain owned by the caller. */
(() => {
  'use strict';
  const registry = window.TodayMerchPreviewRenderers || (window.TodayMerchPreviewRenderers = {});
  if (registry.transfer) return;

  const techniques = Object.freeze({
    digital_transfer: 'digital', digital_transfer_os: 'digital',
    screen_transfer: 'screen', screen_transfer_os: 'screen',
  });
  const materials = Object.freeze({ textile: 0.5, cotton: 0.8, polyester: 0.35 });
  const metadata = new WeakMap();
  const MAX_PIXELS = 4 * 1024 * 1024;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const bounded = (value, fallback, min, max) => typeof value === 'number' && Number.isFinite(value)
    ? clamp(value, min, max) : fallback;
  const aborted = signal => {
    if (signal?.aborted) throw new DOMException('Transfer preview superseded.', 'AbortError');
  };
  const yieldTask = async signal => {
    aborted(signal);
    await new Promise(resolve => setTimeout(resolve, 0));
    aborted(signal);
  };

  // This consumes upstream qualification; it never analyses or repairs artwork.
  function qualify(options = {}) {
    options = options || {};
    const technique = options.techniqueId ?? options.technique;
    const family = typeof technique === 'string' && Object.hasOwn(techniques, technique)
      ? techniques[technique] : null;
    if (options.techniqueId != null && options.technique != null && options.techniqueId !== options.technique)
      return { eligible: false, reason: 'technique_conflict', family: null };
    if (!family) return { eligible: false, reason: 'technique_unqualified', family: null };
    if (options.qualified === false) return { eligible: false, reason: 'offer_unqualified', family };
    const qualification = options.qualification;
    if (!qualification || !Object.hasOwn(qualification, 'status') || qualification.status !== 'allowed') {
      return { eligible: false, reason: 'offer_unqualified', family };
    }
    if (!Object.hasOwn(qualification, 'compatible') || qualification.compatible !== true
      || (options.compatibility !== undefined && (!options.compatibility
        || !Object.hasOwn(options.compatibility, 'status') || options.compatibility.status !== 'compatible'))) {
      return { eligible: false, reason: 'artwork_unqualified', family };
    }
    const classification = options.analysis?.classification;
    if (family === 'screen' && (options.analysis?.status === 'continuous_tone' || classification?.gradients === 'present'
      || classification?.kind === 'continuous_tone')) return { eligible: false, reason: 'screen_requires_flat_colors', family };
    if (family === 'screen' && (options.analysis?.status !== 'detected' || classification?.verified !== true
      || classification.kind !== 'flat_colors' || classification.gradients !== 'absent'
      || !Number.isSafeInteger(options.analysis.colorCount) || options.analysis.colorCount < 1)) {
      return { eligible: false, reason: 'screen_analysis_unqualified', family };
    }
    if (options.material !== undefined && (typeof options.material !== 'string' || !Object.hasOwn(materials, options.material))) {
      return { eligible: false, reason: 'material_unqualified', family };
    }
    return { eligible: true, reason: 'qualified', family };
  }

  function surfaceMap(map) {
    if (!map || !Number.isInteger(map.width) || !Number.isInteger(map.height)
      || map.width < 1 || map.height < 1 || map.width > 4096 || map.height > 4096
      || map.width * map.height > MAX_PIXELS
      || !(map.data instanceof Float32Array || map.data instanceof Uint8Array || map.data instanceof Uint8ClampedArray)
      || map.data.length !== map.width * map.height) return null;
    // Snapshot caller-owned maps before yielding, just as we snapshot the artwork.
    const data = new Float32Array(map.data.length);
    const scale = map.data instanceof Float32Array ? 1 : 1 / 255;
    for (let i = 0; i < data.length; i++) {
      const value = map.data[i] * scale;
      if (!Number.isFinite(value) || value < 0 || value > 1) return null;
      data[i] = value;
    }
    return { width: map.width, height: map.height, data };
  }

  function sample(map, x, y, width, height) {
    if (!map) return 0.5;
    const mx = width > 1 ? x * (map.width - 1) / (width - 1) : (map.width - 1) / 2;
    const my = height > 1 ? y * (map.height - 1) / (height - 1) : (map.height - 1) / 2;
    const x0 = Math.floor(mx), y0 = Math.floor(my);
    const x1 = Math.min(x0 + 1, map.width - 1), y1 = Math.min(y0 + 1, map.height - 1);
    const fx = mx - x0, fy = my - y0, row0 = y0 * map.width, row1 = y1 * map.width;
    const top = map.data[row0 + x0] * (1 - fx) + map.data[row0 + x1] * fx;
    const bottom = map.data[row1 + x0] * (1 - fx) + map.data[row1 + x1] * fx;
    return top * (1 - fy) + bottom * fy;
  }

  function lightOptions(options) {
    const light = options.lighting || {};
    const raw = Array.isArray(light.direction) ? light.direction : [-0.6, -0.8];
    const x = bounded(raw[0], -0.6, -1, 1), y = bounded(raw[1], -0.8, -1, 1);
    const length = Math.hypot(x, y) || 1;
    return { x: x / length, y: y / length, intensity: bounded(light.intensity, 0.45, 0, 1) };
  }

  function substrateLuminance(color) {
    if (typeof color !== 'string' || !/^#[a-f\d]{6}$/i.test(color)) return 0.5;
    return (parseInt(color.slice(1, 3), 16) * 0.2126 + parseInt(color.slice(3, 5), 16) * 0.7152
      + parseInt(color.slice(5, 7), 16) * 0.0722) / 255;
  }

  async function render(sourceCanvas, options = {}) {
    options = options || {};
    const signal = options.signal;
    aborted(signal);
    const Canvas = sourceCanvas?.ownerDocument?.defaultView?.HTMLCanvasElement;
    if (!Canvas || !(sourceCanvas instanceof Canvas)) throw new TypeError('Transfer preview requires an HTMLCanvasElement.');
    const { width, height } = sourceCanvas;
    if (!width || !height || width > 4096 || height > 4096 || width * height > MAX_PIXELS) {
      throw new RangeError('Transfer preview supports 1–4096 pixels per side, up to 4194304 pixels.');
    }
    const output = sourceCanvas.ownerDocument.createElement('canvas');
    output.width = width; output.height = height;
    const context = output.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Transfer preview canvas is unavailable.');
    context.drawImage(sourceCanvas, 0, 0);
    const qualification = qualify(options);
    const finish = result => {
      aborted(signal);
      metadata.set(output, Object.freeze({ ...qualification, ...result, previewOnly: true,
        geometryPreserved: true, effectiveArtworkUnchanged: true,
        limitations: Object.freeze(['simulation_only', 'transfer_process_not_inferred',
          'film_thickness_unverified', 'screen_colors_not_proof', 'no_fabrication_approval']) }));
      return output;
    };
    if (!qualification.eligible) return finish({ applied: false, surfaceApplied: false });

    const surface = options.surface;
    const verifiedSurface = surface?.verified === true && surface.coordinateSpace === 'artwork';
    const shading = verifiedSurface ? surfaceMap(surface.shading) : null;
    const texture = verifiedSurface ? surfaceMap(surface.texture) : null;
    const light = lightOptions(options);
    const material = materials[options.material || 'textile'];
    const luminance = substrateLuminance(options.substrateColor);
    const digital = qualification.family === 'digital';
    const highQuality = options.quality === 'high';
    // A verified finish only adjusts this simulation. No manufacturing finish is inferred.
    const satin = options.finish?.verified === true && options.finish.type === 'satin';
    const sheen = (digital ? 0.008 : 0.003) * (satin ? 1.3 : 1);
    await yieldTask(signal);
    let pixels;
    try { pixels = context.getImageData(0, 0, width, height); }
    catch (error) {
      if (error.name !== 'SecurityError') throw error;
      return finish({ applied: false, surfaceApplied: false, reason: 'pixels_unreadable' });
    }
    const data = pixels.data;
    // Only alpha neighbours are sampled. RGB changes cannot feed back into the shading.
    const alphaAt = (x, y) => x < 0 || x >= width || y < 0 || y >= height ? 0 : data[(y * width + x) * 4 + 3] / 255;
    let sliceStarted = performance.now();
    for (let y = 0; y < height; y++) {
      if ((y & 7) === 0) {
        aborted(signal);
        if (performance.now() - sliceStarted > 7) {
          await yieldTask(signal); sliceStarted = performance.now();
        }
      }
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        if (!data[i + 3]) continue;
        let normalX = alphaAt(x + 1, y) - alphaAt(x - 1, y);
        let normalY = alphaAt(x, y + 1) - alphaAt(x, y - 1);
        if (highQuality) {
          const topLeft = alphaAt(x - 1, y - 1), topRight = alphaAt(x + 1, y - 1);
          const bottomLeft = alphaAt(x - 1, y + 1), bottomRight = alphaAt(x + 1, y + 1);
          normalX = (normalX * 2 + topRight + bottomRight - topLeft - bottomLeft) / 4;
          normalY = (normalY * 2 + bottomLeft + bottomRight - topLeft - topRight) / 4;
        }
        const edge = normalX * light.x + normalY * light.y;
        const planeLight = ((x / Math.max(1, width - 1) - 0.5) * light.x
          + (y / Math.max(1, height - 1) - 0.5) * light.y) * 0.035;
        const fold = (sample(shading, x, y, width, height) - 0.5) * 0.22;
        const grain = (sample(texture, x, y, width, height) - 0.5) * material * (digital ? 0.015 : 0.02);
        const scale = 1 + light.intensity * (planeLight + fold + grain + clamp(edge, -1, 1) * (digital ? 0.024 : 0.016));
        const highlight = light.intensity * sheen * (0.65 + luminance * 0.35)
          * Math.max(0, 0.45 + planeLight * 8 + edge * 0.2);
        for (let channel = 0; channel < 3; channel++) {
          const lit = data[i + channel] * scale;
          data[i + channel] = clamp(lit + (255 - lit) * highlight, 0, 255);
        }
      }
    }
    aborted(signal);
    context.putImageData(pixels, 0, 0);
    return finish({ applied: true, surfaceApplied: Boolean(shading || texture),
      shadingApplied: Boolean(shading), textureApplied: Boolean(texture) });
  }

  registry.transfer = Object.freeze({ render, qualify, getMetadata: canvas => metadata.get(canvas) || null,
    techniques: Object.freeze(Object.keys(techniques)), maxPixels: MAX_PIXELS, version: '1.1.0' });
})();
